import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronDown, File, Folder, Menu, X, FolderOpen, CheckCircle, AlertTriangle, MoreHorizontal, Info } from 'lucide-react';
import { ScanFinding } from '../types/scan.ts';

export interface FileTreeNode {
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: FileTreeNode[];
  findings: ScanFinding[];
}

export interface EnhancedFileTreeNavigationProps {
  findings: ScanFinding[];
  allFiles?: string[]; // Complete list of files from scan metadata
  selectedFile: string | null;
  onFileSelect: (filePath: string | null) => void;
  isProUser: boolean;
  className?: string;
}

// Helper function to get badge color based on highest severity
const getBadgeColor = (findings: ScanFinding[], isProUser: boolean): string => {
  if (findings.length === 0) return 'bg-green-500'; // Clean
  
  // Check if all findings are premium and user is not Pro
  const hasPremiumFindings = findings.some(f => f.is_premium);
  const hasNonPremiumFindings = findings.some(f => !f.is_premium);
  const isLocked = !isProUser && hasPremiumFindings && !hasNonPremiumFindings;
  
  if (isLocked) return 'bg-gray-500'; // Premium-locked
  
  // Filter findings based on user status
  const visibleFindings = isProUser ? findings : findings.filter(f => !f.is_premium);
  if (visibleFindings.length === 0) return 'bg-gray-500'; // All locked
  
  // Determine highest severity
  const hasHigh = visibleFindings.some(f => f.severity === 'high');
  const hasMedium = visibleFindings.some(f => f.severity === 'medium');
  
  if (hasHigh) return 'bg-red-500'; // High
  if (hasMedium) return 'bg-yellow-500'; // Medium
  return 'bg-orange-500'; // Low/Info
};

// Helper function to get badge text
const getBadgeText = (findings: ScanFinding[], isProUser: boolean): string => {
  if (findings.length === 0) return '0';
  
  // Check if all findings are premium and user is not Pro
  const hasPremiumFindings = findings.some(f => f.is_premium);
  const hasNonPremiumFindings = findings.some(f => !f.is_premium);
  const isLocked = !isProUser && hasPremiumFindings && !hasNonPremiumFindings;
  
  if (isLocked) return '🔒';
  
  // Filter findings based on user status
  const visibleFindings = isProUser ? findings : findings.filter(f => !f.is_premium);
  return visibleFindings.length.toString();
};

// Build complete file tree from both findings and complete file list
const buildCompleteFileTree = (findings: ScanFinding[], allFiles: string[] = []): FileTreeNode[] => {
  const fileMap = new Map<string, ScanFinding[]>();
  
  // Group findings by file path
  findings.forEach(finding => {
    const path = finding.file_path;
    if (!fileMap.has(path)) {
      fileMap.set(path, []);
    }
    fileMap.get(path)!.push(finding);
  });
  
  // Create a set of all files (from findings + allFiles)
  const allFilePaths = new Set([
    ...findings.map(f => f.file_path),
    ...allFiles
  ]);
  
  // Build tree structure
  const tree: FileTreeNode[] = [];
  const folderMap = new Map<string, FileTreeNode>();
  
  Array.from(allFilePaths).forEach((filePath) => {
    // Skip invalid file paths
    if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') {
      return;
    }
    
    const fileFindings = fileMap.get(filePath) || [];
    const parts = filePath.split('/').filter(Boolean);
    
    // Skip if no valid parts after splitting
    if (parts.length === 0) {
      return;
    }
    
    if (parts.length === 1) {
      // Root level file
      const fileName = parts[0];
      if (fileName) {
        tree.push({
          name: fileName,
          path: filePath,
          type: 'file',
          findings: fileFindings
        });
      }
    } else {
      // File in subdirectory - create folder if needed
      const folderName = parts[0];
      const fileName = parts[parts.length - 1];
      
      // Skip if folder or file name is undefined/empty
      if (!folderName || !fileName) {
        return;
      }
      
      const fullFolderPath = folderName + '/';
      
      if (!folderMap.has(folderName)) {
        const folderNode: FileTreeNode = {
          name: folderName,
          path: fullFolderPath,
          type: 'folder',
          children: [],
          findings: []
        };
        folderMap.set(folderName, folderNode);
        tree.push(folderNode);
      }
      
      const folderNode = folderMap.get(folderName)!;
      folderNode.children!.push({
        name: fileName,
        path: filePath,
        type: 'file',
        findings: fileFindings
      });
      
      // Add findings to folder total
      folderNode.findings.push(...fileFindings);
    }
  });
  
  // Sort tree: folders first, then files, alphabetically
  const sortNodes = (nodes: FileTreeNode[]) => {
    return nodes.sort((a, b) => {
      // Validate that both nodes have names before comparing
      if (!a.name || !b.name) {
        return 0; // Keep original order if either name is invalid
      }
      
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  };
  
  tree.forEach(node => {
    if (node.children) {
      node.children = sortNodes(node.children);
    }
  });
  
  return sortNodes(tree);
};

// Individual tree node component
const TreeNode: React.FC<{
  node: FileTreeNode;
  selectedFile: string | null;
  onFileSelect: (filePath: string | null) => void;
  isProUser: boolean;
  level: number;
}> = ({ node, selectedFile, onFileSelect, isProUser, level }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0); // Auto-expand root folders
  
  const handleNodeClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    if (node.type === 'folder') {
      setIsExpanded(!isExpanded);
    } else {
      // File selection
      const newSelection = selectedFile === node.path ? null : node.path;
      onFileSelect(newSelection);
    }
  }, [node, selectedFile, onFileSelect, isExpanded]);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNodeClick(e as any);
    } else if (node.type === 'folder') {
      if (e.key === 'ArrowRight' && !isExpanded) {
        setIsExpanded(true);
      } else if (e.key === 'ArrowLeft' && isExpanded) {
        setIsExpanded(false);
      }
    }
  }, [handleNodeClick, node.type, isExpanded]);
  
  const badgeColor = getBadgeColor(node.findings, isProUser);
  const badgeText = getBadgeText(node.findings, isProUser);
  const isSelected = selectedFile === node.path;
  const isFile = node.type === 'file';
  const hasIssues = node.findings.length > 0;
  const Icon = node.type === 'folder' 
    ? (isExpanded ? FolderOpen : Folder)
    : File;
  
  return (
    <div role="treeitem" aria-expanded={node.type === 'folder' ? isExpanded : undefined}>
      <button
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isSelected
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
        style={{ paddingLeft: `${12 + level * 16}px` }}
        onClick={handleNodeClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={`${node.type === 'folder' ? 'Folder' : 'File'}: ${node.name}${node.findings.length > 0 ? `, ${badgeText} issues` : ', no issues'}`}
      >
        {node.type === 'folder' && (
          <span className="flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </span>
        )}
        
        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        
        <span className="flex-1 truncate">{node.name}</span>
        
        {/* Status indicator */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasIssues ? (
            <span
              className={`w-5 h-5 rounded-full text-xs font-bold text-white flex items-center justify-center ${badgeColor}`}
              title={`${badgeText} issue${badgeText !== '1' ? 's' : ''} found`}
            >
              {badgeText}
            </span>
          ) : (
            <CheckCircle className="w-4 h-4 text-green-500" />
          )}
        </div>
      </button>
      
      {/* Render children if folder is expanded */}
      {node.type === 'folder' && isExpanded && node.children && (
        <div className="mt-1">
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              selectedFile={selectedFile}
              onFileSelect={onFileSelect}
              isProUser={isProUser}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EnhancedFileTreeNavigation: React.FC<EnhancedFileTreeNavigationProps> = ({
  findings,
  allFiles = [],
  selectedFile,
  onFileSelect,
  isProUser,
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [displayLimit, setDisplayLimit] = useState(20);

  const fileTree = useMemo(() => {
    const completeTree = buildCompleteFileTree(findings, allFiles);
    
    if (showOnlyIssues) {
      // Filter to only show files and folders with issues
      return completeTree.filter(node => {
        if (node.type === 'file') {
          return node.findings.length > 0;
        } else {
          // For folders, check if any children have issues
          return node.findings.length > 0;
        }
      });
    }
    
    return completeTree;
  }, [findings, allFiles, showOnlyIssues]);

  const displayedTree = useMemo(() => {
    if (displayLimit >= fileTree.length) {
      return fileTree;
    }
    return fileTree.slice(0, displayLimit);
  }, [fileTree, displayLimit]);

  const totalFiles = allFiles.length || findings.length;
  const filesWithIssues = new Set(findings.map(f => f.file_path)).size;
  const cleanFiles = Math.max(0, totalFiles - filesWithIssues);

  // Handle window resize to collapse on mobile
  useEffect(() => {
    // Auto-collapse on mobile screens (simplified approach)
    setIsCollapsed(false); // Start expanded by default
  }, []);

  const toggleCollapsed = () => setIsCollapsed(!isCollapsed);

  return (
    <div className={`bg-gray-800 border-r border-gray-700 ${className} ${isCollapsed ? 'w-0 overflow-hidden' : ''}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-white">Files</h2>
            <div className="text-xs text-gray-400">
              ({totalFiles} total)
            </div>
          </div>
          <button
            onClick={toggleCollapsed}
            className="lg:hidden p-1 text-gray-400 hover:text-white transition-colors"
            title="Toggle file tree"
          >
            {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>

        {/* Summary Stats */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2 p-2 bg-red-900/20 rounded border border-red-700/50">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <div>
              <div className="text-red-300 font-medium">{filesWithIssues}</div>
              <div className="text-red-400">with issues</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-900/20 rounded border border-green-700/50">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <div>
              <div className="text-green-300 font-medium">{cleanFiles}</div>
              <div className="text-green-400">clean</div>
            </div>
          </div>
        </div>

        {/* Issue Badges Legend */}
        <div className="mt-3 p-2 bg-gray-900/50 rounded border border-gray-600/50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-300">Issue Badges</h3>
            <Info className="w-3 h-3 text-gray-400" />
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Critical/High</span>
              <div className="w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">!</div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Medium</span>
              <div className="w-4 h-4 bg-yellow-500 rounded-full text-white text-xs flex items-center justify-center font-bold">!</div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Low/Info</span>
              <div className="w-4 h-4 bg-blue-500 rounded-full text-white text-xs flex items-center justify-center font-bold">!</div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Clean</span>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Premium</span>
              <div className="w-4 h-4 bg-gray-500 rounded-full text-white text-xs flex items-center justify-center font-bold">?</div>
            </div>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="mt-3">
          <button
            onClick={() => setShowOnlyIssues(!showOnlyIssues)}
            className={`w-full text-xs px-3 py-2 rounded transition-colors ${
              showOnlyIssues
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {showOnlyIssues ? 'Show All Files' : 'Show Only Issues'}
          </button>
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1" role="tree">
          {displayedTree.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              selectedFile={selectedFile}
              onFileSelect={onFileSelect}
              isProUser={isProUser}
              level={0}
            />
          ))}
        </div>

        {/* Show More Button */}
        {displayLimit < fileTree.length && (
          <button
            onClick={() => setDisplayLimit(prev => prev + 20)}
            className="w-full mt-4 p-2 text-sm text-gray-400 hover:text-white border border-gray-700 rounded hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
          >
            <MoreHorizontal className="w-4 h-4" />
            Show {Math.min(20, fileTree.length - displayLimit)} more files
          </button>
        )}

        {/* Summary */}
        {displayedTree.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {showOnlyIssues ? 'No files with issues found' : 'No files found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedFileTreeNavigation; 